import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { reportsAPI } from '../services/api'
import { DocumentTextIcon, ArrowLeftIcon, SparklesIcon } from '@heroicons/react/24/outline'
import HealthReportView from '../components/reports/HealthReportView'
import HealthReportPicker from '../components/reports/HealthReportPicker'
import HealthCyclePanel from '../components/health-cycle/HealthCyclePanel'

// VH-67: poll while report status is PENDING / PROCESSING.
const TERMINAL_STATUSES = new Set(['completed', 'failed'])

export default function Reports() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [selectedReportId, setSelectedReportId] = useState(null)
  // VH-227: tabs — 'cycle' (health cycles, replaces the AI analysis tab) | 'list' (weekly reports)
  const [activeTab, setActiveTab] = useState('cycle')

  const { data: reports, isLoading } = useQuery({
    queryKey: ['reports'],
    queryFn: reportsAPI.getAll
  })

  // VH-67: generateWeekly accepts the picker payload; backend may return PENDING for ranges > 1 week.
  const generateMutation = useMutation({
    mutationFn: (payload) => reportsAPI.generateWeekly(payload),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      const id = response?.data?.id
      if (id) setSelectedReportId(id)
    }
  })

  // VH-67: detail query polls until status flips to COMPLETED / FAILED.
  const reportDetailQuery = useQuery({
    queryKey: ['report', selectedReportId],
    queryFn: () => reportsAPI.getById(selectedReportId),
    enabled: !!selectedReportId,
    refetchInterval: (query) => {
      const data = query.state.data?.data || query.state.data
      const status = (data?.status || '').toLowerCase()
      // Legacy rows have no status field — treat as terminal so we don't poll forever.
      if (!status) return false
      return TERMINAL_STATUSES.has(status) ? false : 2000
    },
  })

  const locale = i18n.language === 'de' ? 'de-DE' : i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US'

  // Detail view
  if (selectedReportId) {
    const reportData = reportDetailQuery.data?.data
    const status = (reportData?.status || '').toLowerCase()
    const isPending = !!status && !TERMINAL_STATUSES.has(status)
    const isFailed = status === 'failed'

    return (
      <div className="p-6 max-w-5xl mx-auto">
        <button
          onClick={() => setSelectedReportId(null)}
          className="flex items-center text-primary-600 hover:text-primary-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          {t('common.back')}
        </button>

        {reportDetailQuery.isLoading && !reportData ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">{t('common.loading')}</p>
          </div>
        ) : isPending ? (
          <div className="card">
            <div className="card-body text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-3 text-gray-700 font-medium">{t('reports.picker.generating')}</p>
              <p className="mt-1 text-sm text-gray-500">{t('reports.picker.subtitle')}</p>
            </div>
          </div>
        ) : isFailed ? (
          <div className="card border-l-4 border-danger-500">
            <div className="card-body">
              <p className="text-sm text-danger-700">{t('reports.picker.error')}</p>
            </div>
          </div>
        ) : reportData ? (
          <HealthReportView report={reportData} />
        ) : null}
      </div>
    )
  }

  if (isLoading) {
    return <div className="p-6">{t('common.loading')}</div>
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{t('healthCycle.title')}</h1>
        <p className="mt-2 text-gray-600">{t('healthCycle.subtitle')}</p>
      </div>

      {/* VH-55: Tabs — Health Report (picker + history) / AI Comprehensive Analysis */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab('list')}
            className={`py-2 px-1 border-b-2 text-sm font-medium flex items-center transition-colors ${
              activeTab === 'list'
                ? 'border-purple-600 text-purple-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <DocumentTextIcon className="h-4 w-4 mr-1.5" />
            {t('reports.picker.title')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('cycle')}
            className={`py-2 px-1 border-b-2 text-sm font-medium flex items-center transition-colors ${
              activeTab === 'cycle'
                ? 'border-purple-600 text-purple-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <SparklesIcon className="h-4 w-4 mr-1.5" />
            {t('healthCycle.tab')}
          </button>
        </nav>
      </div>

      {activeTab === 'cycle' ? (
        <HealthCyclePanel />
      ) : (
        <ReportsList
          generateMutation={generateMutation}
          reports={reports}
          setSelectedReportId={setSelectedReportId}
          t={t}
          i18n={i18n}
          locale={locale}
        />
      )}
    </div>
  )
}

function ReportsList({ generateMutation, reports, setSelectedReportId, t, i18n, locale }) {
  return (
    <>
      {/* VH-67: ISO-week range picker replaces the legacy single-button entry point */}
      <HealthReportPicker
        onGenerate={(payload) => generateMutation.mutate(payload)}
        isPending={generateMutation.isPending}
        hasError={generateMutation.isError}
      />

      {reports?.data?.length > 0 ? (
        <div className="grid gap-4">
          {reports.data.map((report) => (
            <div key={report.id} className="card">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-6 w-6 text-primary-600 mr-3" />
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {report.report_type === 'WEEKLY_ACHIEVEMENT'
                          ? t('reports.weekly.weeklyAchievement')
                          : t('reports.healthReport')
                        } - {new Date(report.created_at).toLocaleDateString(i18n.language === 'de' ? 'de-DE' : i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US')}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {report.health_score != null
                          ? `${t('reports.score')}: ${report.health_score}/100`
                          : report.start_date && report.end_date
                            ? `${new Date(report.start_date).toLocaleDateString(locale)} - ${new Date(report.end_date).toLocaleDateString(locale)}`
                            : ''
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {report.health_score != null && (
                      <span className={`badge ${
                        report.health_score >= 80 ? 'badge-success' :
                        report.health_score >= 60 ? 'badge-warning' : 'badge-danger'
                      }`}>
                        {report.health_score >= 80 ? t('reports.excellent') :
                         report.health_score >= 60 ? t('reports.good') : t('reports.needsAttention')}
                      </span>
                    )}
                    <button
                      className="btn-outline"
                      onClick={() => setSelectedReportId(report.id)}
                    >
                      {t('reports.viewReport')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('reports.noReports')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('reports.noReportsDescription')}
          </p>
        </div>
      )}
    </>
  )
}
